"use client";

import { useEffect, useMemo, useState } from "react";
import { useGoogleOAuthSetup } from "../../hooks/use-api.js";
import {
  getStoredCodeVerifier,
  clearStoredCodeVerifier,
} from "../../lib/oauth-utils.js";
import { Card, CardContent, CardHeader, CardTitle } from "../card.js";
import { Alert, AlertDescription } from "../alert.js";
import { Button } from "../button.js";
import { Calendar, CheckCircle, AlertCircle, Loader2 } from "lucide-react";
import { useRouter } from "next/navigation";
import { useToast } from "../../hooks/use-toast.js";
import { getBrowserSupabaseClient } from "../../lib/supabase-browser.js";

interface GoogleSetupPageProps {
  /**
   * Optional path to navigate back to setup.
   * Defaults to `/unifiedtime/setup`.
   */
  backToSetupPath?: string;
}

interface TokenInfo {
  accountType: string;
  email?: string;
  expiresIn?: number;
  scopes?: string[];
  timestamp: string;
}

export default function GoogleSetupPage({ backToSetupPath }: GoogleSetupPageProps = {}) {
  const router = useRouter();
  const { toast } = useToast();
  const googleOAuthSetup = useGoogleOAuthSetup();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [tokenInfo, setTokenInfo] = useState<TokenInfo | null>(null);

  const resolvedBackPath = useMemo(() => backToSetupPath || "/unifiedtime/setup", [backToSetupPath]);

  useEffect(() => {
    const handleOAuthCallback = async () => {
      // Check for OAuth callback parameters in URL fragment
      const urlHash = new URLSearchParams(window.location.hash.substring(1));
      const code = urlHash.get("code");
      const state = urlHash.get("state");
      const errorParam = urlHash.get("error");

      if (errorParam) {
        setError(decodeURIComponent(errorParam));

        return;
      }

      if (code) {
        // Get session after OAuth redirect to ensure it's available
        const supabase = getBrowserSupabaseClient();
        try {
          // This will get the session from localStorage/cookies
          await supabase.auth.getSession();
        } catch (error) {
          console.warn("Error getting session:", error);
        }
        
        // Small delay to allow session to restore after OAuth redirect
        await new Promise(resolve => setTimeout(resolve, 500));
        await exchangeCodeForToken(code, state || undefined);
      } else {
        // No OAuth callback, redirect to setup
        router.push(resolvedBackPath);
      }
    };

    handleOAuthCallback();
  }, [router, resolvedBackPath]);

  const exchangeCodeForToken = async (code: string, state?: string) => {
    setIsLoading(true);
    setError(null);
    setSuccessMessage(null);
    setTokenInfo(null);

    try {
      const isSecondAccount = state === "secondAccount";
      const codeVerifier = getStoredCodeVerifier(isSecondAccount);

      if (!codeVerifier) {
        throw new Error("PKCE code verifier missing");
      }

      const result = await googleOAuthSetup.mutateAsync({
        code,
        code_verifier: codeVerifier,
        isSecondAccount,
      });

      clearStoredCodeVerifier(isSecondAccount);

      const accountType = isSecondAccount ? "secondary" : "primary";
      const successMsg = `${accountType.charAt(0).toUpperCase() + accountType.slice(1)} Google account connected successfully!`;
      setSuccessMessage(successMsg);

      // Extract token info from the response if available
      const tokenData = result?.data || result;
      const info: TokenInfo = {
        accountType: accountType.charAt(0).toUpperCase() + accountType.slice(1),
        email: tokenData?.email || tokenData?.user_email,
        expiresIn: tokenData?.expiresIn || tokenData?.expires_in,
        scopes: tokenData?.scopes ? (typeof tokenData.scopes === 'string' ? tokenData.scopes.split(' ') : tokenData.scopes) : undefined,
        timestamp: new Date().toISOString(),
      };
      setTokenInfo(info);

      toast({
        title: "Success",
        description: successMsg,
      });
    } catch (err: any) {
      const errorMessage = err.message || "Failed to connect Google calendar";
      setError(errorMessage);
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleBackToSetup = () => {
    router.push(resolvedBackPath);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Card className="w-96">
          <CardContent className="flex flex-col items-center space-y-4 p-6">
            <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
            <p>Connecting Google Calendar...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="text-center space-y-4">
        <h1 className="text-4xl font-extrabold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
          Google Calendar Setup
        </h1>
        <p className="text-muted-foreground text-base">
          Connect your Google Calendar to UnifiedTime
        </p>
      </div>

      {successMessage && (
        <Alert>
          <CheckCircle className="h-4 w-4" />
          <AlertDescription>{successMessage}</AlertDescription>
        </Alert>
      )}

      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <Card className="border-2 border-blue-200 bg-gradient-to-br from-blue-50 to-blue-100">
        <CardHeader>
          <CardTitle className="flex items-center space-x-2 text-blue-900">
            <span className="p-2 bg-blue-200 rounded-lg">
              <Calendar className="h-5 w-5 text-blue-700" />
            </span>
            <span>Google Calendar Connection</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-4">
            {successMessage && tokenInfo ? (
              <div className="space-y-4">
                <p className="text-sm text-muted-foreground">
                  Your Google calendar is successfully connected. You can now
                  view your events in the unified calendar.
                </p>
                
                {/* Token Details Section */}
                <div className="bg-white rounded-lg p-4 border border-blue-200 space-y-3">
                  <h3 className="font-semibold text-sm text-blue-900">Connection Details</h3>
                  
                  <div className="grid grid-cols-2 gap-3 text-xs">
                    <div>
                      <p className="text-muted-foreground font-medium">Account Type</p>
                      <p className="text-foreground font-mono">{tokenInfo.accountType}</p>
                    </div>
                    
                    {tokenInfo.email && (
                      <div>
                        <p className="text-muted-foreground font-medium">Email</p>
                        <p className="text-foreground font-mono truncate">{tokenInfo.email}</p>
                      </div>
                    )}
                    
                    {tokenInfo.expiresIn && (
                      <div>
                        <p className="text-muted-foreground font-medium">Token Expires In</p>
                        <p className="text-foreground font-mono">{Math.round(tokenInfo.expiresIn / 3600)} hours</p>
                      </div>
                    )}
                    
                    <div>
                      <p className="text-muted-foreground font-medium">Connected At</p>
                      <p className="text-foreground font-mono text-xs">{new Date(tokenInfo.timestamp).toLocaleTimeString()}</p>
                    </div>
                  </div>
                  
                  {tokenInfo.scopes && tokenInfo.scopes.length > 0 && (
                    <div>
                      <p className="text-muted-foreground font-medium text-xs mb-2">Granted Scopes ({tokenInfo.scopes.length})</p>
                      <div className="flex flex-wrap gap-1">
                        {tokenInfo.scopes.map((scope, idx) => (
                          <span key={idx} className="inline-block bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded">
                            {scope.split('/').pop() || scope}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                
                <Button onClick={handleBackToSetup} className="w-full bg-blue-600 hover:bg-blue-700 text-white">
                  Back to Setup
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                <p className="text-sm text-muted-foreground">
                  There was an issue connecting your Google calendar. Please try
                  again.
                </p>
                <Button onClick={handleBackToSetup} className="w-full bg-blue-600 hover:bg-blue-700 text-white">
                  Back to Setup
                </Button>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
